// Figma: 5287:18364 — see stories/blocks/about-hero-marquee.stories.tsx
"use client";

import type { AboutHeroImage } from "@/components/blocks/about-hero";
import { MarqueeTrack } from "@/components/blocks/marquee-track";
import { cn } from "@/lib/utils";

type HeroMarqueeTile = AboutHeroImage & {
  aspect: "square" | "wide";
};

interface AboutHeroMarqueeProps {
  rows: {
    topRow: HeroMarqueeTile[];
    bottomRow: HeroMarqueeTile[];
  };
}

const TILE_BASE =
  "rounded-ds-l border-grey-800/30 relative shrink-0 overflow-hidden border";

const TILE_SIZES = {
  square:
    "h-[200px] w-[200px] md:h-[260px] md:w-[260px] lg:h-[315px] lg:w-[316px]",
  wide: "h-[200px] w-[300px] md:h-[260px] md:w-[400px] lg:h-[315px] lg:w-[529px]",
} as const;

function HeroMarqueeRow({
  tiles,
  direction,
  animationClassName,
}: {
  tiles: HeroMarqueeTile[];
  direction: "ltr" | "rtl";
  animationClassName: string;
}) {
  const loop = [...tiles, ...tiles];

  return (
    <MarqueeTrack
      direction={direction}
      animationClassName={animationClassName}
      trackClassName="gap-2"
    >
      {loop.map((tile, index) => {
        const isDuplicate = index >= tiles.length;
        return (
          <li
            key={`${tile.src}-${index}`}
            aria-hidden={isDuplicate}
            className={cn(TILE_BASE, TILE_SIZES[tile.aspect])}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={tile.src}
              alt={isDuplicate ? "" : tile.alt}
              className="absolute inset-0 size-full object-cover"
              loading={index < tiles.length ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={index < 4 ? "high" : "auto"}
            />
          </li>
        );
      })}
    </MarqueeTrack>
  );
}

export function AboutHeroMarquee({ rows }: AboutHeroMarqueeProps) {
  const { topRow, bottomRow } = rows;

  return (
    <div className="flex flex-col gap-2">
      <HeroMarqueeRow
        tiles={topRow}
        direction="rtl"
        animationClassName="motion-safe:animate-[values-marquee_80s_linear_infinite]"
      />
      <HeroMarqueeRow
        tiles={bottomRow}
        direction="ltr"
        animationClassName="motion-safe:animate-[values-marquee_90s_linear_infinite_reverse]"
      />
    </div>
  );
}

/** Build two marquee rows from the 8-image mosaic set (about-us stories). */
export function buildAboutHeroMarqueeRows(images: {
  topLeftWide: AboutHeroImage;
  topMid1: AboutHeroImage;
  topMid2: AboutHeroImage;
  topRight: AboutHeroImage;
  bottomLeft: AboutHeroImage;
  bottomMid1: AboutHeroImage;
  bottomFeatureWide: AboutHeroImage;
  bottomRight: AboutHeroImage;
}) {
  const topRow: HeroMarqueeTile[] = [
    { ...images.topLeftWide, aspect: "wide" },
    { ...images.topMid1, aspect: "square" },
    { ...images.topMid2, aspect: "square" },
    { ...images.topRight, aspect: "square" },
    { ...images.bottomLeft, aspect: "square" },
    { ...images.topMid1, aspect: "square" },
    { ...images.bottomFeatureWide, aspect: "wide" },
  ];

  const bottomRow: HeroMarqueeTile[] = [
    { ...images.bottomLeft, aspect: "square" },
    { ...images.bottomMid1, aspect: "square" },
    { ...images.bottomFeatureWide, aspect: "wide" },
    { ...images.bottomRight, aspect: "square" },
    { ...images.topMid2, aspect: "square" },
    { ...images.topRight, aspect: "square" },
  ];

  return { topRow, bottomRow };
}
